import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import TouchAppIcon from '@material-ui/icons/TouchApp';
import type { TooltipRenderProps } from 'react-joyride';
import { Hotspot } from './Hotspot';
import { type StepData, useStepAction } from './interaction';

const useStyles = makeStyles(theme => ({
  root: {
    width: 340,
    maxWidth: 'calc(100vw - 32px)',
    padding: theme.spacing(2.5, 2.5, 2),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
  },
  centered: { width: 460, padding: theme.spacing(3.5, 3.5, 3) },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  close: {
    margin: theme.spacing(-1, -1, 0, 0),
    color: theme.palette.text.secondary,
  },
  body: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    '& p': { margin: 0 },
    '& p + p': { marginTop: theme.spacing(1) },
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2.5),
    gap: theme.spacing(1),
  },
  dots: { display: 'flex', gap: 5 },
  dot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: theme.palette.divider,
  },
  dotActive: { backgroundColor: theme.palette.primary.main },
  buttons: { display: 'flex', gap: theme.spacing(1), marginLeft: 'auto' },
  hint: {
    display: 'flex',
    whiteSpace: 'nowrap',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.75, 1.25),
    borderRadius: 8,
    fontWeight: 600,
    color: theme.palette.primary.main,
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(0, 213, 190, 0.12)'
        : 'rgba(0, 150, 137, 0.08)',
  },
}));

/**
 * Tour card in the portal's own MUI theme (teal, Inter, rounded cards) instead of Joyride's.
 * The first step reads as an invitation (« Plus tard » / « Faire le tour »), the last one ends.
 * Joyride's English aria-labels (« Next », « Skip »…) are replaced by the visible French ones.
 * A step with an action (interaction.ts) replaces « Suivant » by what to do, with a hotspot on
 * the element, until the user has done it.
 */
export function TourTooltip(props: TooltipRenderProps) {
  const {
    backProps,
    closeProps,
    controls,
    index,
    isLastStep,
    primaryProps,
    size,
    skipProps,
    step,
    tooltipProps,
  } = props;
  const classes = useStyles();
  const isFirst = index === 0;
  const centered = step.placement === 'center';

  const { action, element, done } = useStepAction(step, index, controls);
  const waiting = Boolean(action) && !done;

  // A step can name its buttons (guides.tsx: « Voir comment », « Compris »).
  const labels = (step.data ?? {}) as StepData;
  let primaryLabel = 'Suivant';
  if (isLastStep) primaryLabel = 'Terminer';
  else if (isFirst && centered) primaryLabel = 'Faire le tour';
  if (labels.primaryLabel) primaryLabel = labels.primaryLabel;
  // A single-step guide only needs its primary button.
  const showSkip = isFirst && !isLastStep;

  return (
    <Paper
      {...tooltipProps}
      elevation={8}
      className={`${classes.root} ${centered ? classes.centered : ''}`}
    >
      <div className={classes.header}>
        <Typography variant={centered ? 'h6' : 'subtitle1'} component="h2">
          <b>{step.title}</b>
        </Typography>
        <IconButton
          {...closeProps}
          aria-label="Fermer la visite"
          title="Fermer la visite"
          size="small"
          className={classes.close}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
      <Typography variant="body2" component="div" className={classes.body}>
        {step.content}
      </Typography>
      <div className={classes.footer}>
        {!isFirst && !isLastStep && !waiting && (
          <Box className={classes.dots} aria-hidden>
            {Array.from({ length: size }, (_, i) => (
              <span
                key={i}
                className={`${classes.dot} ${
                  i <= index ? classes.dotActive : ''
                }`}
              />
            ))}
          </Box>
        )}
        <div className={classes.buttons}>
          {showSkip && (
            <Button
              {...skipProps}
              aria-label="Plus tard"
              title="Plus tard"
              size="small"
              variant="outlined"
            >
              Plus tard
            </Button>
          )}
          {!isFirst && !isLastStep && (
            <Button
              {...backProps}
              aria-label="Précédent"
              title="Précédent"
              size="small"
            >
              Précédent
            </Button>
          )}
          {waiting && action ? (
            <Typography variant="body2" className={classes.hint} role="status">
              <TouchAppIcon fontSize="small" />
              {action.hint}
            </Typography>
          ) : (
            action?.kind !== 'click' && (
              <Button
                {...primaryProps}
                aria-label={primaryLabel}
                title={primaryLabel}
                size="small"
                variant="contained"
                color="primary"
                disableElevation
              >
                {primaryLabel}
              </Button>
            )
          )}
        </div>
      </div>
      {waiting && element && <Hotspot element={element} />}
    </Paper>
  );
}
